import { type AlertColor } from '@mui/material';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { BreakpointsInPx } from '../enums/BreakpointsInPx';
import { ELocalStorageItems } from '../enums/ELocalStorageItems';
import { EStylings } from '../enums/EStylings';
import { type ActionsEnumDeprecate } from '../enums/actionsEnumDeprecate';
import { type ViewsEnumDeprecate } from '../enums/viewsEnumDeprecate';
import HGeneric from '../helpers/HGeneric';
import type BreadcrumbModel from '../models/BreadcrumbModel';
import { type IFundDTO } from '../models/IFundDTO';
import { type IFundTabs } from '../models/IFundTabs';
import { type TLocales } from '../models/TLocales';

interface AppSliceState {
  appName: string;
  locale?: TLocales;
  localeDict?: Record<string, string>;
  activeView: {
    type?: ViewsEnumDeprecate;
  };
  activeAction: {
    type?: ActionsEnumDeprecate;
  };
  breadcrumbs: BreadcrumbModel[];
  breadcrumbReplacementTextDict: Record<string, string>;
  breadcrumbReplacementTextDictTimestamp: number;
  dashboardOutletDimensions: {
    h: number;
    w: number;
    left: number;
    columnW: number;
    columnLeft: number;
    padding: number;
    maxPageModuleW: number;
  };
  funds: IFundDTO[];
  fundTabs: IFundTabs[];
  headerDimensions: { h: number; w: number; narrowScreen: boolean };
  sidebar: {
    isExpanded: boolean;
    dim: {
      w: number;
    };
  };
  snackbar: {
    isVisible: boolean;
    message?: string;
    autoHideDuration: number;
    severity: AlertColor;
    timestamp: number;
  };
  title: string;
  processLoading: number;
  isTouchscreen: boolean;
  factsheetId?: string;
}

const initialState: AppSliceState = {
  appName: 'neXDos',
  locale: undefined,
  localeDict: undefined,
  activeView: {
    type: undefined
  },
  activeAction: {
    type: undefined
  },
  breadcrumbs: [],
  breadcrumbReplacementTextDict: {},
  breadcrumbReplacementTextDictTimestamp: 0,
  dashboardOutletDimensions: {
    h: 0,
    w: 0,
    left: 0,
    columnW: 0,
    columnLeft: 0,
    padding: 0,
    maxPageModuleW: 0
  },
  funds: [],
  fundTabs: [],
  headerDimensions: { h: 0, w: 0, narrowScreen: false },
  sidebar: {
    isExpanded: window.localStorage.getItem(ELocalStorageItems.SIDEBAR_TOGGLE)
      ? window.localStorage.getItem(ELocalStorageItems.SIDEBAR_TOGGLE) === 'true'
      : true,
    dim: {
      w: 0
    }
  },
  snackbar: {
    isVisible: false,
    message: undefined,
    autoHideDuration: 7000,
    severity: 'info',
    timestamp: 0
  },
  title: '',
  processLoading: 0,
  isTouchscreen: false
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    updateActiveView: (state, action) => {
      state.activeView.type = action.payload as ViewsEnumDeprecate | undefined;
    },
    updateActiveAction: (state, action) => {
      state.activeAction.type = action.payload as ActionsEnumDeprecate | undefined;
    },
    updateDashboardDim: (state, action: PayloadAction<Partial<{ w: number; h: number; left: number }>>) => {
      if (action.payload.h !== undefined) state.dashboardOutletDimensions.h = action.payload.h;
      if (action.payload.w !== undefined) state.dashboardOutletDimensions.w = action.payload.w;
      if (action.payload.left !== undefined) state.dashboardOutletDimensions.left = action.payload.left;

      if (action.payload.w !== undefined) {
        state.dashboardOutletDimensions.columnW =
          Math.min(action.payload.w, EStylings.MAX_PAGE_MODULE_WIDTH) -
          2 * (document.documentElement.clientWidth >= 640 ? 4 : 1) * HGeneric.getRemInPx();
        state.dashboardOutletDimensions.columnLeft =
          (state.dashboardOutletDimensions.w - state.dashboardOutletDimensions.columnW) / 2 +
          state.dashboardOutletDimensions.left;
        state.dashboardOutletDimensions.padding =
          state.dashboardOutletDimensions.columnLeft - state.dashboardOutletDimensions.left;
        state.dashboardOutletDimensions.maxPageModuleW = Math.min(
          state.dashboardOutletDimensions.w,
          EStylings.MAX_PAGE_MODULE_WIDTH
        );
      }
    },
    updateHeaderDim: (state, action: PayloadAction<Partial<{ h: number; w: number }>>) => {
      if (action.payload.h) state.headerDimensions.h = action.payload.h;
      if (action.payload.w) state.headerDimensions.w = action.payload.w;
      state.headerDimensions.narrowScreen = (action.payload.w ?? 0) < BreakpointsInPx.BREAKPOINT_2;
    },
    updateSidebarDim: (state, action: PayloadAction<Partial<{ w: number }>>) => {
      if (action.payload.w !== undefined) state.sidebar.dim.w = action.payload.w;
    },
    toggleSidebar: (state) => {
      state.sidebar.isExpanded = !state.sidebar.isExpanded;
      window.localStorage.setItem(ELocalStorageItems.SIDEBAR_TOGGLE, state.sidebar.isExpanded.toString());
    },
    updatePageTitle: (state, action) => {
      // TO DEPRECATE SINCE FUTURE PAGE RENAME WILL OCCUR VIA PageModule
      if (action.payload) {
        state.title = (action.payload as string) + ' - ' + state.appName;
      } else {
        state.title = state.appName;
      }
      // document.title = state.title;
    },
    addBreadcrumbTextReplacement: (state, action) => {
      const pair = action.payload as [string, string];
      state.breadcrumbReplacementTextDict[pair[0]] = pair[1]?.replace(/(<([^>]+)>)/gi, '');
      state.breadcrumbReplacementTextDictTimestamp = new Date().getTime();
    },
    purgeBreadcrumbTextReplacementDictForOutlets: () => {
      // Might be redundant
      // state.breadcrumbReplacementTextDict = {};
    },
    updateProcessLoading: (state, action) => {
      state.processLoading += action.payload ? 1 : -1;
    },
    updateSnackbar: (
      state,
      action: PayloadAction<{
        message?: string;
        autoHideDuration?: number;
        severity?: 'error' | 'info' | 'success' | 'warning';
        isVisible?: boolean;
      }>
    ) => {
      state.snackbar = { ...state.snackbar, ...action.payload, timestamp: Date.now() };
      state.snackbar.isVisible = !!action.payload.message;
    },
    updateIsTouchscreen: (state, action: PayloadAction<{ isTouchscreen: boolean }>) => {
      state.isTouchscreen = action.payload.isTouchscreen;
    },
    updateLocale: (state, action: PayloadAction<{ locale: TLocales }>) => {
      state.locale = action.payload.locale;
      window.localStorage.setItem(ELocalStorageItems.LOCALE, action.payload.locale);
      document.documentElement.setAttribute('lang', action.payload.locale);
    },
    updateLocaleDict: (state, action: PayloadAction<{ localeDict: Record<string, string> }>) => {
      state.localeDict = action.payload.localeDict;
    },
    updateFactsheetId: (state, action: PayloadAction<{ factsheetId: string | undefined }>) => {
      state.factsheetId = action.payload.factsheetId;
    },
    updateFunds: (state, action: PayloadAction<{ funds: IFundDTO[] }>) => {
      state.funds = action.payload.funds;
    },
    updateFundTabs: (state, action: PayloadAction<{ fundTabs: IFundTabs[] }>) => {
      state.fundTabs = action.payload.fundTabs;
    }
  }
});

export const {
  updateActiveView,
  updateActiveAction,
  updateDashboardDim,
  toggleSidebar,
  updatePageTitle,
  addBreadcrumbTextReplacement,
  purgeBreadcrumbTextReplacementDictForOutlets,
  updateProcessLoading,
  updateSnackbar,
  updateIsTouchscreen,
  updateHeaderDim,
  updateSidebarDim,
  updateLocale,
  updateLocaleDict,
  updateFactsheetId,
  updateFunds,
  updateFundTabs
} = appSlice.actions;

export default appSlice.reducer;
